<script module lang="ts">
	export interface RFInspectorSidepanelProps {
		classNames?: string;
		title?: string;
		left?: boolean;
		right?: boolean;
		onresize?: (width: number, height: number) => void;
		onclosepanel?: () => void;
		fullscreen?: boolean;
		open?: boolean;
	}
</script>

<script lang="ts">
	import parseBool from '@src/utils/bool';
	import { onMount } from 'svelte';
	import RfInspectorControl from './rf-inspector-control.svelte';

	/**
	 * Props
	 */
	let {
		classNames = '',
		title = '',
		left = true,
		right = false,
		onresize = () => {},
		onclosepanel = () => {},
		open = true,
		fullscreen = false,
	}: RFInspectorSidepanelProps = $props();

	/**
	 * State
	 */
	let isExpanded = $state(open);
	let element: HTMLElement | null = $state(null);

	/**
	 * Derived values
	 */
	const panelClasses = $derived([
		'rf-inspector-sidepanel',
		classNames,
		{
			'rf-inspector-sidepanel--left': parseBool(left),
			'rf-inspector-sidepanel--right': parseBool(right),
			'rf-inspector-sidepanel--expanded': isExpanded,
		},
	]);

	/**
	 * Methods
	 */
	const togglePanel = () => {
		isExpanded = !isExpanded;
		if (!isExpanded) {
			onclosepanel();
		}
	};

	const handleResize = () => {
		if (!element) return;
		const bounds = element.getBoundingClientRect();
		onresize(bounds.width, bounds.height);
	};

	/**
	 * Effects
	 */
	$effect(() => {
		// Handle fullscreen state
		isExpanded = !fullscreen;
	});

	$effect(() => {
		// Sync with external open state
		isExpanded = open;
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		if (!element) return;

		// Setup resize observer
		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(element);

		// Cleanup on destroy
		return () => {
			resizeObserver.disconnect();
		};
	});
</script>

<div bind:this={element} class={panelClasses}>
	<div class="rf-inspector-sidepanel__controls">
		<span class="rf-inspector-sidepanel__controls__title">{title}</span>

		{#if isExpanded}
			<RfInspectorControl icon="open-panel" onclick={togglePanel} ariaLabel="Retract" />
		{:else if !isExpanded}
			<RfInspectorControl icon="close-panel" onclick={togglePanel} ariaLabel="Expand" />
		{/if}
	</div>

	<div class="rf-inspector-sidepanel__content">
		<slot></slot>
	</div>
</div>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-inspector-sidepanel {
		flex-shrink: 0;
		z-index: 13;
		width: auto;
		height: calc(100vh - 16px);
		padding: 8px;
		background-color: var(--rf-inspector-control-bg-color, rgba(35, 35, 35, 0.5));
		backdrop-filter: blur(4px);
		border: solid var(--rf-inspector-control-border-color, rgba(150, 150, 150, 0.1)) 1px;
		border-radius: 8px;
		overflow: hidden;
		overflow-y: auto;
		box-sizing: border-box;

		@media screen and (max-width: breakpoint.$s) {
			height: auto;
			width: 100%;
			order: 1;
		}

		&--expanded {
			width: min(33svw, 512px);
			@media screen and (max-width: breakpoint.$s) {
				width: 100svw;
				height: calc(50dvh - 16px);
			}
		}

		&__controls {
			display: flex;
			justify-content: space-between;
			align-items: center;
			position: sticky;
			background-color: var(--rf-inspector-card-bg-color, rgba(50, 50, 50, 0.5));
			padding: var(--rf-spacing-xs, 4px);
			border-radius: 8px;
			backdrop-filter: blur(2px);
			top: 0;
			z-index: 10;
			color: white;

			.rf-inspector-sidepanel--right & {
				flex-direction: row-reverse;
			}

			.rf-inspector-sidepanel--expanded & {
				margin-bottom: 8px;
			}

			&__title {
				display: none;
				font-family: var(--rf-font-family);
				font-size: 1.25rem;
				padding-left: var(--rf-spacing-xs, 4px);

				.rf-inspector-sidepanel--right & {
					padding-left: 0;
					padding-right: var(--rf-spacing-xs, 4px);
				}

				.rf-inspector-sidepanel--expanded & {
					display: block;
				}

				@media screen and (max-width: breakpoint.$s) {
					display: block;
				}
			}

			.rf-inspector-control {
				svg {
					transform: rotate(0deg);
					.rf-inspector-sidepanel--expanded & {
						transform: rotate(0deg);
					}

					.rf-inspector-sidepanel--right & {
						transform: rotate(180deg);
					}

					.rf-inspector-sidepanel--right.rf-inspector-sidepanel--expanded & {
						transform: rotate(180deg);
					}
					@media screen and (max-width: breakpoint.$s) {
						transform: rotate(-90deg);
					}
					.rf-inspector-sidepanel--expanded & {
						@media screen and (max-width: breakpoint.$s) {
							transform: rotate(270deg);
						}
					}
				}
			}
		}

		&__content {
			display: none;
			.rf-inspector-sidepanel--expanded & {
				display: flex;
				flex-direction: column;
				gap: var(--rf-spacing-s, 8px);
			}
		}
	}
</style>
